import React, { useCallback, useEffect, useState } from 'react';

import { Badge, Group, Text } from '@mantine/core';
import CurrentPackagesTableCell from 'components/AthletePackages/CurrentPackagesTableCell';
import Table from 'components/Table';
import dayjs from 'dayjs';
import useAthleteAttendance from 'hooks/reports/athletes/useAthleteAttendance';
import { useNavigate } from 'react-router-dom';
import { CellProps } from 'react-table';
import { AthleteAttendanceReport } from 'types/generated';

import { SearchFilters } from '../types';

const AttendanceTable = ({
  searchFilters,
  setOrderingValue,
}: {
  searchFilters: SearchFilters;
  setOrderingValue: React.Dispatch<React.SetStateAction<string>>;
}): JSX.Element => {
  const navigate = useNavigate();
  const [tableData, setTableData] = useState<AthleteAttendanceReport[]>([]);
  const [ordering, setOrdering] = useState('-checkedInEvents');
  const [ascending, setAscending] = useState(true);

  const { data, fetchNextPage, hasNextPage, isSuccess, isError, isLoading, isFetchingNextPage } =
    useAthleteAttendance({
      events: !!searchFilters.eventTypes.length ? searchFilters.eventTypes.map(e => e.eventTypeId) : [],
      dateStart: searchFilters.dateRange[0]
        ? dayjs(searchFilters.dateRange[0]).format('YYYY-MM-DD')
        : undefined,
      dateEnd: searchFilters.dateRange[1]
        ? dayjs(searchFilters.dateRange[1]).format('YYYY-MM-DD')
        : undefined,
      athleteStatuses: searchFilters.athleteStatuses,
      ordering: ascending ? ordering : `-${ordering}`,
    });

  const handleHeaderClick = (header: string): void => {
    setOrdering(header);
    setAscending(a => {
      setOrderingValue(!a ? header : `-${header}`);
      return !a;
    });
  };

  const handleNameClick = useCallback(
    (athlete: AthleteAttendanceReport): void => {
      navigate(`/users/athletes/${athlete.athleteId}/general-info`);
    },
    [navigate],
  );

  const getOpacity = (hasHoldPackages: string): number => {
    return hasHoldPackages === 'True' ? 0.6 : 1;
  };

  useEffect(() => {
    if (!isLoading && data) {
      setTableData(data.pages.map(page => page.data.results).flat());
    }
  }, [isLoading, data, setTableData]);

  const columns = [
    {
      Header: 'Name',
      accessor: 'firstName' as const,
      Cell: ({ row }: CellProps<AthleteAttendanceReport>) => (
        <Group noWrap>
          <Text
            weight='bold'
            underline
            sx={{
              cursor: 'pointer',
              width: 'fit-content',
              opacity: getOpacity(row.original.hasHoldPackages),
            }}
            onClick={() => handleNameClick(row.original)}
          >
            {row.original.fullName}
          </Text>
          {row.original.hasHoldPackages === 'True' && (
            <Badge color='gray' size='sm'>
              Hold
            </Badge>
          )}
        </Group>
      ),
    },
    {
      Header: 'Status',
      accessor: 'athleteStatus' as const,
      disableSortBy: true,
      Cell: ({ row }: CellProps<AthleteAttendanceReport>) => (
        <Text sx={{ opacity: getOpacity(row.original.hasHoldPackages) }}>{row.original.athleteStatus}</Text>
      ),
    },
    {
      Header: 'Email',
      accessor: 'email' as const,
      disableSortBy: true,
      Cell: ({ row }: CellProps<AthleteAttendanceReport>) => (
        <Text sx={{ opacity: getOpacity(row.original.hasHoldPackages) }}>{row.original.email}</Text>
      ),
    },
    {
      Header: 'Class Attendance',
      accessor: 'checkedInClasses' as const,
      Cell: ({ row }: CellProps<AthleteAttendanceReport>) => (
        <Text sx={{ opacity: getOpacity(row.original.hasHoldPackages) }}>
          {row.original.checkedInClasses}
        </Text>
      ),
    },
    {
      Header: 'Class Reservations',
      accessor: 'reservedClasses' as const,
      Cell: ({ row }: CellProps<AthleteAttendanceReport>) => (
        <Text sx={{ opacity: getOpacity(row.original.hasHoldPackages) }}>
          {row.original.reservedClasses}
        </Text>
      ),
    },
    {
      Header: 'App Attendance',
      accessor: 'checkedInAppointments' as const,
      Cell: ({ row }: CellProps<AthleteAttendanceReport>) => (
        <Text sx={{ opacity: getOpacity(row.original.hasHoldPackages) }}>
          {row.original.checkedInAppointments}
        </Text>
      ),
    },
    {
      Header: 'App Reservations',
      accessor: 'reservedAppointments' as const,
      Cell: ({ row }: CellProps<AthleteAttendanceReport>) => (
        <Text sx={{ opacity: getOpacity(row.original.hasHoldPackages) }}>
          {row.original.reservedAppointments}
        </Text>
      ),
    },
    {
      Header: 'Total Attendance',
      accessor: 'checkedInEvents' as const,
      Cell: ({ row }: CellProps<AthleteAttendanceReport>) => (
        <Text sx={{ opacity: getOpacity(row.original.hasHoldPackages) }}>
          {row.original.checkedInEvents}
        </Text>
      ),
    },
    {
      Header: 'Total Reservations',
      accessor: 'reservedEvents' as const,
      Cell: ({ row }: CellProps<AthleteAttendanceReport>) => (
        <Text sx={{ opacity: getOpacity(row.original.hasHoldPackages) }}>{row.original.reservedEvents}</Text>
      ),
    },
    {
      Header: 'Current Packages',
      accessor: 'currentPackages' as const,
      disableSortBy: true,
      Cell: ({ row }: CellProps<AthleteAttendanceReport>) => (
        <CurrentPackagesTableCell currentPackages={row.original.currentPackages ?? []} />
      ),
    },
  ];

  return (
    <>
      <Table
        tableData={tableData}
        columns={columns}
        isSuccess={isSuccess}
        isLoading={isLoading}
        isError={isError}
        isFetchingNextPage={isFetchingNextPage}
        hasNextPage={!!hasNextPage}
        fetchNextPage={fetchNextPage}
        clickableHeaders={['firstName', 'email', 'checkedInEvents', 'reservedEvents']}
        handleHeaderClick={handleHeaderClick}
        ordering={ordering}
        ascending={ascending}
      />
    </>
  );
};

export default AttendanceTable;
